import * as React from 'react';
import { NonRouterModal } from './Modal';
import { Box, Button, Divider, FormControl, Input, InputLabel, TextField, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DataGrid, GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import { AlertProps } from '@mui/material/Alert';

import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';

import { TestCombination, getTestCombination } from '../types/TestCombinations';
import { FieldType, IfacePropertySchema, InterfaceProperty } from "../types/Interface";
import { WorkshopProductProperty } from "../types/WorkshopProductProperty";
import { Product, getProduct } from '../types/Product';
import { WorkshopProduct } from '../types/WorkshopProducts';

import { useSnackbar } from '../../contexts/SnackBarContext';

export const PropertyJSONErrorModal = ({ workshopProduct, interfaceProperty, workshopProductProperty,
    workshopProductPropertySubmitHandler, openState, setOpenState }:
    {
        workshopProduct?: WorkshopProduct,
        interfaceProperty?: InterfaceProperty,
        workshopProductProperty?: WorkshopProductProperty,
        workshopProductPropertySubmitHandler: (wpp: WorkshopProductProperty) => void,
        openState: boolean,
        setOpenState: React.Dispatch<React.SetStateAction<boolean>>
    }) => {
    const { showSnackbar } = useSnackbar();

    const [product, setProduct] = React.useState<Product>();
    const [propValue, setPropValue] = React.useState<string>('');
    const [testCombination, setTestCombination] = React.useState<TestCombination>();

    const showUserFeedback = React.useCallback((msg: string, severity: AlertProps['severity'] = 'error') => {
        showSnackbar(msg, severity);
    }, [showSnackbar]);

    let propertySchema: IfacePropertySchema | undefined;
    if (interfaceProperty && interfaceProperty.fieldSchema) {
        try {
            propertySchema = JSON.parse(interfaceProperty.fieldSchema) as IfacePropertySchema;
        } catch (e) {
            console.warn('Invalid JSON in interfaceProperty.fieldSchema for id', interfaceProperty.id, e);
            propertySchema = undefined;
        }
    } else {
        propertySchema = undefined;
    }

    React.useEffect(() => {
        if (workshopProductProperty && workshopProductProperty.value) {
            setPropValue(workshopProductProperty.value);
        }
        else {
            setPropValue('');
        }
    }, [workshopProductProperty])

    React.useEffect(() => {
        async function fetch() {
            try {
                if (workshopProduct === undefined || workshopProduct.productId === 0) return;
                if (!workshopProductProperty) return;

                const productRequest = await getProduct(workshopProduct.productId, new AbortController());
                const testCombinationsRequest = await getTestCombination(workshopProductProperty.testCombinationId, new AbortController());

                if (productRequest.failed) {
                    productRequest.errorText && showUserFeedback(productRequest.errorText);
                    return;
                }
                if (testCombinationsRequest.failed) {
                        testCombinationsRequest.errorText && showUserFeedback(testCombinationsRequest.errorText);
                    return;
                }

                setProduct(productRequest.result);
                setTestCombination(testCombinationsRequest.result);
            } catch (error) {
                showUserFeedback(String(error));
            }
        };

        fetch();
    }, [workshopProduct, workshopProductProperty, showUserFeedback]);

    const saveHandler = () => {
        if (workshopProductProperty && interfaceProperty) {
            if (propValue === workshopProductProperty?.value) {
                return;
            }
            workshopProductPropertySubmitHandler({ ...workshopProductProperty, interfacePropertyId: interfaceProperty.id, value: propValue });
        }
        setPropValue('');
        setOpenState(false);
    };

    const deleteHandler = () => {
        if (workshopProductProperty && interfaceProperty) {
            workshopProductPropertySubmitHandler({ ...workshopProductProperty, interfacePropertyId: interfaceProperty.id, value: '' });
        }
        setPropValue('');
        setOpenState(false);
    };

    if (!interfaceProperty || interfaceProperty.fieldType !== FieldType.Table || !propertySchema) {
        return <NonRouterModal title='Error' openState={openState} setOpenState={setOpenState} />;
    }

    return <NonRouterModal title='Please Correct invalid JSON Data' openState={openState} setOpenState={setOpenState} cancel={false}
        alertComponent={undefined}
        buttons={[
            <Button onClick={deleteHandler} startIcon={<DeleteIcon />}>{'Delete'}</Button>,
            <Button onClick={saveHandler} startIcon={<CheckIcon />}>{'Confirm'}</Button>
            ]}
        >
        <form id='addWsProductForm'>
            <Stack direction={'column'} spacing={20}>
                <Grid container columnSpacing={30} rowSpacing={20}>
                    <Grid size={9}>
                        <FormControl disabled fullWidth>
                            <InputLabel shrink htmlFor='productName'>{"Product Name"}</InputLabel>
                            <Input id='productName' value={product?.name}></Input>
                        </FormControl>
                    </Grid>
                    <Grid size={3}>
                        <FormControl disabled fullWidth>
                            <InputLabel shrink htmlFor='productVersion'>{"Product Version"}</InputLabel>
                            <Input id='productVersion' value={workshopProduct?.productVersion}></Input>
                        </FormControl>
                    </Grid>
                    <Grid size={12}>
                        <FormControl disabled fullWidth>
                            <InputLabel shrink htmlFor='testcombination'>{"Combination"}</InputLabel>
                            <Input id='testcombination' value={testCombination?.combinationName}></Input>
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl disabled fullWidth>
                            <InputLabel shrink htmlFor='ifacePropGroup'>{'Group'}</InputLabel>
                            <Input id='ifacePropGroup' value={interfaceProperty?.group} disabled={true} />
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl disabled fullWidth>
                            <InputLabel shrink htmlFor='ifacePropName'>{'Property'}</InputLabel>
                            <Input id='ifacePropName' value={interfaceProperty?.name} disabled={true} />
                        </FormControl>
                    </Grid>
                </Grid>

                <Divider />

                <Grid container rowSpacing={20}>
                    <Grid size={12}>
                        <FormControl disabled fullWidth>
                            <TextField multiline
                                label={<Typography color={'red'} variant='body2'>
                                    {'Current Value (contains error)'}
                                </Typography>}
                                slotProps={{ inputLabel: { shrink: true }, }}
                                value={workshopProductProperty?.value}
                                />
                        </FormControl>
                    </Grid>
                </Grid>

                <Divider />

                <FormControl fullWidth>
                    {propertySchema && workshopProductProperty ? <EditableTable2 tableSchema={propertySchema} workshopProductProperty={workshopProductProperty}
                        interfaceProperty={interfaceProperty} setPropValue={setPropValue} />
                        : <></>}
                </FormControl>

            </Stack>
        </form></NonRouterModal>
}

const newEmptyRow = (columnDefinitons: GridColDef[], id: number) => {
    const rowObject: { [key: string]: any } = { id: id };

    columnDefinitons.forEach((columnDefiniton, index) => {
        rowObject[columnDefiniton.field] = '';
    });

    return rowObject;
}

const removeRow = (rows: Array<{ [key: string]: any }>, id: number) => rows.filter(row => row.id !== id);

const updateRow = (rows: Array<{ [key: string]: any }>, updatedRow: { [key: string]: any }) => {
    const rowIndex = rows.findIndex(row => row.id === updatedRow.id);

    console.log('updateRow updatedRow: ', updatedRow);
    console.log('updateRow rowIndex: ', rowIndex);
    console.log('updateRow: ', [...rows.slice(0, rowIndex), updatedRow, ...rows.slice(rowIndex + 1, rows.length)])

    return [...rows.slice(0, rowIndex), updatedRow, ...rows.slice(rowIndex + 1, rows.length)];
}

const parseTableRows = (columnDefinitions: GridColDef[], rows?: Array<string[]>) => {
    const rowObjects: { [key: string]: any }[] = [];

    if (!rows || !Array.isArray(rows)) return rowObjects;

    rows.map((row, index) => {
        let rowObject: { [key: string]: any } = newEmptyRow(columnDefinitions, index);

        columnDefinitions.forEach((columnDefinition, index) =>
            rowObject[columnDefinition.field] = row[index]);

        rowObjects.push(rowObject);
    })

    return rowObjects;
}

// Build a safe JSON string for the table: array-of-arrays. Use JSON.stringify to properly escape values.
const tableRows2String = (rows: Array<{ [key: string]: any }>) => {
    // For each row object, take values excluding the leading 'id' and the trailing action column ('deleteRow') if present.
    const arr = rows.map(row => {
        const rowValues = Object.values(row);
        // slice out id (first) and last column (actions) which is typically the delete column.
        const valuesOnly = rowValues.slice(1, rowValues.length - 1);
        return valuesOnly.map(v => (v === undefined || v === null) ? '' : String(v));
    });
    return JSON.stringify(arr);
}

const EditableTable2 = ({ interfaceProperty, tableSchema, setPropValue, workshopProductProperty }:
    {
        tableSchema: IfacePropertySchema, workshopProductProperty: WorkshopProductProperty, interfaceProperty: InterfaceProperty,
        setPropValue: React.Dispatch<React.SetStateAction<string>>
    }) => {
    const columnDefintions: GridColDef[] = tableSchema.columns.map((column: string) => {
        return {
            field: column,
            headerName: column,
            flex: 1,
            editable: true,
        }
    });

    console.log('json: ', workshopProductProperty?.value);

    const [tableRows, setTableRows] = React.useState<{ [key: string]: any }[]>([]);   // there is no need to initialize this, as it will be set in useEffect

    const deleteActionColumn: GridColDef = {
        field: 'deleteRow',
        type: "actions",
        renderHeader: () => <GridActionsCellItem color="inherit" icon={<AddIcon />} label={'Add row'} onClick={() => { addRow(); }} />,
        getActions: ({ id }) => [
            <GridActionsCellItem icon={<DeleteIcon />} label={'Delete'} onClick={() => { deleteRow(Number(id)); }} color='inherit' />,
        ]
    }
    columnDefintions.push(deleteActionColumn);

    // Safely parse stored JSON for the table. If the stored value is invalid JSON, log a warning and use an empty table.
    React.useEffect(() => {
        let parsed: string[][] | undefined = undefined;
        if (workshopProductProperty?.value && workshopProductProperty.jsonError !== true) {
            try {
                parsed = JSON.parse(workshopProductProperty.value) as string[][];
            } catch (e) {
                workshopProductProperty.jsonError = true;
                console.warn('Invalid JSON in workshopProductProperty.value for interfacePropertyId', interfaceProperty.id, e, workshopProductProperty.value);
                parsed = undefined;
            }
        }
        setTableRows(parseTableRows(columnDefintions, parsed));
    }, [workshopProductProperty])       // columnDefintions changes every render, so including here creates a loop... find cleaner way to break this loop

    const addRow = () => {
        if (tableRows.length >= tableSchema.maxRows) return;
        const nextRowId = tableRows.length > 0 ? Math.max(...tableRows.map(row => row.id)) + 1 : 1; // the previous method of counting rows by length is not reliable, as rows can be deleted
        setTableRows([...tableRows, newEmptyRow(columnDefintions, nextRowId)]);
    }

    const deleteRow = (id: number) => {
        if (tableRows.length <= tableSchema.minRows && tableSchema.isRequired) return;
        const updatedTableRows = removeRow(tableRows, id);
        setPropValue(tableRows2String(updatedTableRows));
        setTableRows(updatedTableRows);
    }

    const onRowUpdate = (newRow: { [key: string]: any }, oldRow: { [key: string]: any }) => {
        const updatedTableRows = updateRow(tableRows, newRow);
        setPropValue(tableRows2String(updatedTableRows));
        setTableRows(updatedTableRows);
        return newRow;
    }

    return <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Box
            sx={{
                '& .required-theme--header': {
                    color: 'rgba(255, 0, 0, 1)',
                },
                '& .optional-theme--header': {
                    color: 'rgba(0, 0, 0, 1)',
                },
            }}
        >
            <Typography variant="body1" color="error" sx={{ mb: 1 }}>{"Invalid JSON data, please re-enter the data. (note: the errored data will be removed)"}</Typography>
            <DataGrid
                columns={columnDefintions}
                rows={tableRows}
                showColumnVerticalBorder
                showCellVerticalBorder
                hideFooter
                processRowUpdate={onRowUpdate}
                onProcessRowUpdateError={(error) => { console.log('ERROR: ', error); }}
                getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'}
                editMode='row' />
        </Box>
    </div>
}
